import React, { useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { EditorView, Decoration, ViewPlugin, WidgetType, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { EditorState, Compartment, StateField, StateEffect } from '@codemirror/state';
import { markdown } from '@codemirror/lang-markdown';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { syntaxTree } from '@codemirror/language';

// --- Vorgeladene Drag-Images (verhindert macOS Weltkugel-Symbol) ---
// MUSS am Modul-Level erstellt werden, damit image.complete === true bei dragstart.
// Siehe: https://www.sam.today/blog/html5-dnd-globe-icon
const DRAG_REMOVE_IMG = new Image();
(() => {
  const c = document.createElement('canvas');
  c.width = 28; c.height = 28;
  const ctx = c.getContext('2d');
  ctx.beginPath(); ctx.arc(14, 14, 12, 0, Math.PI * 2);
  ctx.fillStyle = '#D9455F'; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.moveTo(8, 14); ctx.lineTo(20, 14); ctx.stroke();
  DRAG_REMOVE_IMG.src = c.toDataURL();
})();
// Fallback: transparentes 1x1 Pixel (falls Custom-Image nicht geladen)
const EMPTY_DRAG_IMG = new Image(1, 1);
EMPTY_DRAG_IMG.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

// --- Bild-Widget ---
class ImageWidget extends WidgetType {
  constructor(src, alt) { super(); this.src = src; this.alt = alt; }
  toDOM() {
    const img = document.createElement('img');
    img.src = this.src.startsWith('http') ? this.src : `/attachments/${this.src}`;
    img.alt = this.alt;
    img.style.cssText = 'max-width:100%;border-radius:6px;margin:6px 0;display:block';
    return img;
  }
}

// --- Wiki-Link Widget ---
// Supports [[id]] and [[id|display text]]
class WikiLinkWidget extends WidgetType {
  constructor(linkId, displayText, title) {
    super();
    this.linkId = linkId;
    this.displayText = displayText;
    this.title = title;
  }
  toDOM() {
    const el = document.createElement('span');
    el.className = 'wikilink-rendered';
    el.style.cssText = 'color:var(--accent);cursor:pointer;font-weight:500;text-decoration:underline;text-decoration-color:color-mix(in srgb, var(--accent) 30%, transparent);text-underline-offset:2px;text-decoration-thickness:1.5px';
    el.addEventListener('mouseenter', () => { el.style.textDecorationColor = 'var(--accent)'; });
    el.addEventListener('mouseleave', () => { el.style.textDecorationColor = 'color-mix(in srgb, var(--accent) 30%, transparent)'; });
    el.dataset.linkId = this.linkId;
    // Show: display text > title > id
    if (this.displayText) {
      el.textContent = this.displayText;
    } else if (this.title) {
      el.textContent = `${this.linkId}: ${this.title}`;
    } else {
      el.textContent = this.linkId;
    }
    return el;
  }
  ignoreEvent() { return false; }
  eq(other) {
    return this.linkId === other.linkId && this.displayText === other.displayText && this.title === other.title;
  }
}

// Global ref for allZettel so the plugin can access it
let _allZettelForPlugin = [];

// --- Shared helper: render highlight text as HTML (images, bold, italic, marks) ---
function renderHighlightHtml(inner) {
  const rawText = inner.replace(/^>\s*/gm, '').replace(/"/g, '');
  // Extract images before HTML escaping (placeholders protect URLs)
  const images = [];
  const textWithPlaceholders = rawText.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, url) => {
    images.push({ alt, url });
    return `\x05IMG${images.length - 1}\x06`;
  });
  function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  let html = escapeHtml(textWithPlaceholders);
  html = html.replace(/==([^=]+)==/g, '\x01$1\x02');
  html = html.replace(/(?<!=)=([^=]+)=(?!=)/g, '\x03$1\x04');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/\x01([^\x02]*)\x02/g, '<mark style="background:var(--highlight-bg);color:var(--text-primary);border-radius:3px;padding:1px 3px;font-style:normal">$1</mark>');
  html = html.replace(/\x03([^\x04]*)\x04/g, '<mark style="background:color-mix(in srgb,var(--highlight-bg) 50%,transparent);color:var(--text-primary);border-radius:3px;padding:1px 3px;font-style:normal">$1</mark>');
  // Restore images
  html = html.replace(/\x05IMG(\d+)\x06/g, (_, idx) => {
    const img = images[parseInt(idx)];
    return `<img src="${escapeHtml(img.url)}" alt="${escapeHtml(img.alt)}" style="max-width:100%;border-radius:6px;margin:6px 0;display:block" />`;
  });
  return html;
}

// --- Details Widget (named class so eq() works and open-state is preserved) ---
class DetailsWidget extends WidgetType {
  constructor(summary, inner, ref, onOpenInBibliothek, onRemoveHighlight, onMoveHighlight, onDragOutHighlight) {
    super();
    this.summary = summary;
    this.inner = inner;
    this.ref = ref;
    this.onOpenInBibliothek = onOpenInBibliothek;
    this.onRemoveHighlight = onRemoveHighlight;
    this.onMoveHighlight = onMoveHighlight;
    this.onDragOutHighlight = onDragOutHighlight;
  }

  // When summary + inner + ref are the same, reuse the existing DOM node → open state preserved
  eq(other) {
    return other instanceof DetailsWidget &&
           other.summary === this.summary &&
           other.inner === this.inner &&
           other.ref === this.ref;
  }

  // Called when eq() returns false — update existing DOM in-place so open state is preserved
  updateDOM(dom) {
    if (dom.tagName !== 'DETAILS') return false;
    // Update the content div (second child after summary)
    const contentDiv = dom.querySelector('div');
    if (!contentDiv) return false;
    contentDiv.innerHTML = renderHighlightHtml(this.inner);
    // CRITICAL-3: Update callback references on the DOM element to avoid stale closures
    if (this.ref) {
      dom._hlCallbacks = {
        onRemoveHighlight: this.onRemoveHighlight,
        onMoveHighlight: this.onMoveHighlight,
        onDragOutHighlight: this.onDragOutHighlight,
        onOpenInBibliothek: this.onOpenInBibliothek
      };
    }
    return true; // DOM reused, open state preserved
  }

  toDOM() {
    const details = document.createElement('details');
    details.setAttribute('contenteditable', 'false'); // Verhindert Browser-URL-Injection beim Drag
    details.style.cssText = 'margin:8px 0;border:1px solid var(--border, #e0e0e0);border-radius:8px;background:color-mix(in srgb, var(--accent) 4%, var(--surface, #f8f8f8));overflow:hidden;padding:0;transition:border-color 0.15s,background 0.15s';
    const sum = document.createElement('summary');
    sum.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:0.8rem;color:var(--text-muted);font-weight:500;list-style:none;display:flex;align-items:center;gap:6px;transition:color 0.15s;border-bottom:1px solid transparent';
    sum.addEventListener('mouseenter', () => { sum.style.color = 'var(--accent)'; });
    sum.addEventListener('mouseleave', () => { sum.style.color = details.open ? 'var(--accent)' : 'var(--text-muted)'; });
    const arrow = document.createElement('span');
    arrow.textContent = '▶';
    arrow.style.cssText = 'font-size:0.6em;transition:transform 0.15s;opacity:0.6';
    sum.appendChild(arrow);
    const summaryText = document.createElement('span');
    summaryText.textContent = this.summary;
    summaryText.style.cssText = 'flex:1';
    sum.appendChild(summaryText);
    // Entfernen-Button für Highlight-Blöcke (mit data-ref)
    if (this.ref && this.onRemoveHighlight) {
      const removeBtn = document.createElement('span');
      removeBtn.className = 'hl-embed-remove-btn';
      removeBtn.title = 'Highlight entfernen';
      removeBtn.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      const ref = this.ref;
      // CRITICAL-3: Use indirection via _hlCallbacks to avoid stale closures
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const cb = details._hlCallbacks?.onRemoveHighlight;
        if (cb) cb(ref);
      });
      sum.appendChild(removeBtn);
    }
    details.appendChild(sum);
    const content = document.createElement('div');
    content.style.cssText = 'padding:8px 12px 10px;font-size:0.88rem;line-height:1.75;color:var(--text-secondary)';
    content.innerHTML = renderHighlightHtml(this.inner);
    details.appendChild(content);
    // Link zur Bibliothek — <span> statt <a> um URL-Drag (Weltkugel-Symbol) zu vermeiden
    if (this.ref && this.onOpenInBibliothek) {
      const link = document.createElement('span');
      link.textContent = 'In Bibliothek bearbeiten';
      link.style.cssText = 'display:block;padding:4px 12px 8px;font-size:0.75rem;color:var(--accent);opacity:0.6;cursor:pointer;transition:opacity 0.15s';
      link.addEventListener('mouseenter', () => { link.style.opacity = '1'; });
      link.addEventListener('mouseleave', () => { link.style.opacity = '0.6'; });
      const ref = this.ref;
      // CRITICAL-3: Use indirection via _hlCallbacks
      link.addEventListener('click', (e) => {
        e.stopPropagation();
        const cb = details._hlCallbacks?.onOpenInBibliothek;
        if (cb) cb(ref);
      });
      details.appendChild(link);
    }
    details.addEventListener('toggle', () => {
      arrow.style.transform = details.open ? 'rotate(90deg)' : '';
      details.style.borderColor = details.open ? 'color-mix(in srgb, var(--accent) 60%, transparent)' : 'var(--border, #e0e0e0)';
      sum.style.color = details.open ? 'var(--accent)' : 'var(--text-muted)';
      sum.style.borderBottomColor = details.open ? 'var(--border, #e0e0e0)' : 'transparent';
    });

    // --- Drag & Drop innerhalb des Editors (nur für Highlight-Blöcke) ---
    if (this.ref && this.onMoveHighlight) {
      details.setAttribute('draggable', 'true');
      details.dataset.highlightRef = this.ref;
      // Roberto: Tooltip für Discoverability
      details.title = 'Highlight verschieben (ziehen) oder aus Editor herausziehen zum Entfernen';
      const hlRef = this.ref;
      let customGhost = null;
      let editorBodyRect = null; // Gespeichert bei dragstart, weil element bei dragend evtl. detached ist
      let dragHandledByDrop = false; // Flag: wurde der Drop intern verarbeitet?
      let onDragOverGlobal = null; // document-dragover-Listener für Ghost-Positionierung
      let onDragOverPrevent = null; // document-dragover-Listener nur für preventDefault (verhindert Globe-Cursor)
      let onDropPrevent = null;     // document-drop-Listener nur für preventDefault

      // Distanz vom nächsten Rand eines Rects (0 wenn innerhalb)
      const distFromRect = (cx, cy, r) => {
        if (!r) return 0;
        const dx = Math.max(r.left - cx, 0, cx - r.right);
        const dy = Math.max(r.top  - cy, 0, cy - r.bottom);
        return Math.sqrt(dx * dx + dy * dy);
      };

      details.addEventListener('dragstart', (e) => {
        e.stopPropagation();
        // Entfernt auto-hinzugefügte URL-Daten des Browsers (wegen <a href="#"> im Widget).
        // text/uri-list mit der localhost-URL würde macOS veranlassen, das Globus-Symbol anzuzeigen.
        e.dataTransfer.clearData('text/uri-list');
        e.dataTransfer.clearData('text/html');
        e.dataTransfer.clearData('text/plain');
        e.dataTransfer.setData('application/highlight-editor-move', hlRef);
        // 'copyMove' erlaubt beide Modi — der dropEffect wird pro Zielzone gesetzt
        e.dataTransfer.effectAllowed = 'copyMove';
        dragHandledByDrop = false;

        // Editor-Bounds bei dragstart erfassen (Element könnte bei dragend detached sein)
        const editorBody = details.closest('.editor-body');
        editorBodyRect = editorBody ? editorBody.getBoundingClientRect() : null;

        // Quell-Karte dezent ausblenden
        details.style.opacity = '0.25';
        details.style.transition = 'opacity 0.15s';

        // Vorgeladenes Drag-Image verwenden (verhindert macOS Weltkugel)
        // DRAG_REMOVE_IMG ist am Modul-Level erstellt → image.complete === true
        const dragImg = DRAG_REMOVE_IMG.complete ? DRAG_REMOVE_IMG : EMPTY_DRAG_IMG;
        e.dataTransfer.setDragImage(dragImg, 14, 14);

        // Custom Ghost: einzelne Karte die dem Cursor folgt
        const rawText = this.inner.replace(/^>\s*/gm, '').replace(/"/g, '');
        const lines = rawText.split('\n').filter(l => l.trim());
        const clip = (s) => s.length > 60 ? s.slice(0, 60) + '…' : s;
        const previewLines = lines.slice(0, 2).map(clip);

        customGhost = document.createElement('div');
        customGhost.style.cssText = [
          'position:fixed', 'top:0', 'left:0',
          'pointer-events:none', 'z-index:99999',
          'width:240px',
          'padding:9px 13px',
          'background:var(--color-bg-elevated,#fff)',
          'border:1.5px solid var(--color-primary,#e94560)',
          'border-radius:8px',
          'font-size:11px', 'line-height:1.5',
          'color:var(--color-text-secondary,#555)',
          'display:flex', 'align-items:flex-start', 'gap:7px',
          'box-shadow:0 6px 20px rgba(0,0,0,0.15)',
          'overflow:hidden',
          // scale+opacity haben kurze Transition damit sie sanft der Distanz folgen
          'transition:opacity 80ms ease-out,transform 80ms ease-out',
          'will-change:transform,opacity',
          'transform-origin:top left',
        ].join(';');
        customGhost.innerHTML =
          '<span style="flex-shrink:0;margin-top:1px">📎</span>' +
          '<span>' + previewLines.map(l => l.replace(/</g, '&lt;')).join('<br>') + '</span>';

        document.body.appendChild(customGhost);

        // Verhindert Globe-/No-Drop-Cursor des OS: Gesamtes Dokument als gültiges Drop-Ziel markieren.
        // SEPARAT von onDragOverGlobal — kein Zugriff auf customGhost, kein Race-Condition-Risiko.
        onDragOverPrevent = (ev) => {
          if (ev.dataTransfer.types.includes('application/highlight-editor-move')) {
            ev.preventDefault();
            ev.dataTransfer.dropEffect = 'move';
          }
        };
        onDropPrevent = (ev) => {
          if (ev.dataTransfer.types.includes('application/highlight-editor-move')) {
            ev.preventDefault(); // Verhindert Browser-Default-Drop-Handling
          }
        };
        document.addEventListener('dragover', onDragOverPrevent, false);
        document.addEventListener('drop', onDropPrevent, false);

        // Cursor-Tracking: Position + proportionales Shrink+Fade je nach Distanz zum Editor
        onDragOverGlobal = (ev) => {
          if (!customGhost) return; // Sicherheitsguard
          const cx = ev.clientX, cy = ev.clientY;
          const dist = distFromRect(cx, cy, editorBodyRect);
          // 0px Distanz → scale 1.0, opacity 1.0
          // 160px Distanz → scale 0.45, opacity 0.12
          const t = Math.min(dist / 160, 1);
          const scale   = 1 - t * 0.55;         // ease-out: schnell schrumpfen
          const opacity = 1 - t * 0.88;          // ease-in: noch schneller verblassen

          customGhost.style.transform = `translate(${cx + 14}px,${cy - 22}px) scale(${scale.toFixed(3)})`;
          customGhost.style.opacity   = opacity.toFixed(3);
        };
        document.addEventListener('dragover', onDragOverGlobal);
      });

      details.addEventListener('dragend', (e) => {
        if (onDragOverGlobal)  { document.removeEventListener('dragover', onDragOverGlobal,  false); onDragOverGlobal  = null; }
        if (onDragOverPrevent) { document.removeEventListener('dragover', onDragOverPrevent, false); onDragOverPrevent = null; }
        if (onDropPrevent)     { document.removeEventListener('drop',     onDropPrevent,     false); onDropPrevent     = null; }

        details.style.opacity = '';
        details.style.transition = '';

        // Wenn der Drop intern im Editor verarbeitet wurde (Reorder), Ghost entfernen
        if (dragHandledByDrop) {
          if (customGhost?.parentNode) customGhost.remove();
          customGhost = null;
          editorBodyRect = null;
          return;
        }

        const rect = editorBodyRect;
        editorBodyRect = null;
        const x = e.clientX, y = e.clientY;
        // clientX/Y === 0 → ESC-Abbruch — nicht als drag-out werten
        if (x === 0 && y === 0) {
          if (customGhost?.parentNode) customGhost.remove();
          customGhost = null;
          return;
        }

        const droppedOutside = !rect || x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
        if (droppedOutside && customGhost) {
          // Ghost ist bereits skaliert/verblasst — kurzes Finale zum scale(0)
          customGhost.style.transition = 'transform 0.18s ease-in,opacity 0.15s ease-in';
          requestAnimationFrame(() => {
            customGhost.style.transform = customGhost.style.transform.replace(/scale\([^)]+\)/, 'scale(0)');
            customGhost.style.opacity = '0';
          });
          // Quell-Karte gleichzeitig ausblenden
          details.style.transition = 'opacity 0.22s ease-in';
          details.style.opacity = '0';
          const ghostToClean = customGhost;
          setTimeout(() => { if (ghostToClean?.parentNode) ghostToClean.remove(); }, 220);
          customGhost = null;
          // CRITICAL-3: Aktuelle Callback-Referenz über _hlCallbacks
          setTimeout(() => {
            const dragOutCb = details._hlCallbacks?.onDragOutHighlight;
            if (dragOutCb) dragOutCb(hlRef);
          }, 200);
        } else {
          if (customGhost?.parentNode) customGhost.remove();
          customGhost = null;
        }
      });

      // Listener für intern verarbeitete Drops (setzt Flag damit dragend nicht löscht)
      details._markDropHandled = () => { dragHandledByDrop = true; };

      // ESC-Taste bricht Drag ab und räumt Custom-Ghost auf
      details.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && customGhost?.parentNode) {
          customGhost.remove();
          customGhost = null;
        }
      });
    }

    // CRITICAL-3: Callbacks auf dem DOM-Element speichern für stale-closure-freie Indirektion
    if (this.ref) {
      details._hlCallbacks = {
        onRemoveHighlight: this.onRemoveHighlight,
        onMoveHighlight: this.onMoveHighlight,
        onDragOutHighlight: this.onDragOutHighlight,
        onOpenInBibliothek: this.onOpenInBibliothek
      };
    }

    return details;
  }

  ignoreEvent(e) {
    // Drag events: let the browser handle DnD natively
    if (e.type && e.type.startsWith('drag')) return true;
    // Remove-Button clicks: let the handler fire
    if (e.target && e.target.closest && e.target.closest('.hl-embed-remove-btn')) return true;
    // Summary clicks: let the browser handle the <details> toggle
    if (e.target && e.target.closest && e.target.closest('summary')) return true;
    // Link clicks: let the link handler fire
    if (e.target && e.target.closest && e.target.closest('a')) return true;
    // Highlight blocks (with data-ref) are always preview-only — ignore ALL events
    if (this.ref) return true;
    // Regular <details> blocks: pass clicks to CodeMirror for editing
    return false;
  }
}

// --- Highlight-Block Cursor-Skip-Filter entfernt ---
// Mit {{highlight:ref}} Einzeiler-Markern nicht mehr nötig — CM6 behandelt
// single-line Decoration.replace atomar.

// --- Details Block Decorations via StateField ---
// CM6-Regel: Multi-Zeilen Decoration.replace() NUR über StateField erlaubt (nicht ViewPlugin, nicht decorations.of(fn)).
// Callbacks werden über ein module-level Mutable-Container-Objekt bereitgestellt,
// das vom LiveEditor-Effekt aktuell gehalten wird.

// Focus-Tracking: StateEffect + StateField so dass buildDetailsDecosFromState
// bei Blur alle Blöcke als Widget zeigt (kein Raw-Text nach Wegklicken).
const focusChangedEffect = StateEffect.define();
const hasFocusField = StateField.define({
  create: () => true,
  update(focused, tr) {
    for (const e of tr.effects) if (e.is(focusChangedEffect)) return e.value;
    return focused;
  },
});

function buildDetailsDecosFromState(state, callbacks) {
  const widgets = [];
  const doc = state.doc;
  const focused = state.field(hasFocusField, false) ?? true;
  const cursorLine = focused ? doc.lineAt(state.selection.main.head).number : -1;
  const fullText = doc.toString();

  const { onOpenInBibliothek, onRemoveHighlight, onDragOutHighlight, onMoveHighlight } = callbacks;

  // Nur plain <details> Blöcke (OHNE data-ref) — Highlight-Blöcke nutzen jetzt {{highlight:ref}}
  const detailsRegex = /<details(?:\s+data-ref="([^"]*)")?\s*>\s*\n\s*<summary>(.*?)<\/summary>\s*\n([\s\S]*?)\n\s*<\/details>/g;
  let dm;
  while ((dm = detailsRegex.exec(fullText)) !== null) {
    const ref = dm[1] || null;
    if (ref) continue; // Highlight-Blöcke werden jetzt von livePreviewPlugin via {{highlight:ref}} gehandhabt
    const summary = dm[2];
    const inner = dm[3].trim();
    const startLine = doc.lineAt(dm.index).number;
    const endPos = Math.min(dm.index + dm[0].length - 1, doc.length - 1);
    const endLine = doc.lineAt(Math.max(0, endPos)).number;

    const cursorInBlock = cursorLine >= startLine && cursorLine <= endLine;

    if (!cursorInBlock) {
      const firstLine = doc.line(startLine);
      if (firstLine.text.length > 0) {
        const replaceFrom = firstLine.from;
        const blockEnd = doc.line(endLine).to;
        widgets.push(Decoration.replace({
          widget: new DetailsWidget(summary, inner, null, onOpenInBibliothek, onRemoveHighlight, onMoveHighlight, onDragOutHighlight)
        }).range(replaceFrom, blockEnd));
      }
    }
  }

  widgets.sort((a, b) => a.from - b.from || a.to - b.to);
  return Decoration.set(widgets, true);
}

function makeDetailsField(callbacksContainer) {
  return StateField.define({
    create(state) {
      return buildDetailsDecosFromState(state, callbacksContainer);
    },
    update(decos, tr) {
      const hasFocusChange = tr.effects.some(e => e.is(focusChangedEffect));
      if (tr.docChanged || tr.selectionSet || hasFocusChange) {
        return buildDetailsDecosFromState(tr.state, callbacksContainer);
      }
      return decos;
    },
    provide: f => EditorView.decorations.from(f),
  });
}

// --- Live Preview Plugin (Inline-Formatierung + {{highlight:ref}} Einzeiler-Widgets) ---
function livePreviewPlugin(callbacksContainer) {
  return ViewPlugin.fromClass(class {
    constructor(view) {
      // Cache the full text so selectionSet updates don't call doc.toString() again
      this._fullText = view.state.doc.toString();
      this._lastCursorLine = -1;
      this.decorations = this.buildDecos(view);
    }

    update(update) {
      if (update.docChanged) {
        // Document changed: refresh the full-text cache and rebuild everything
        this._fullText = update.view.state.doc.toString();
        this._lastCursorLine = -1;
        this.decorations = this.buildDecos(update.view);
      } else if (update.selectionSet || update.viewportChanged || update.focusChanged) {
        // Cursor moved or focus changed: only rebuild if the active line changed.
        // Moving within the same line never changes which tokens are rendered/raw.
        const newLine = update.view.hasFocus
          ? update.view.state.doc.lineAt(update.view.state.selection.main.head).number
          : -1;
        if (newLine !== this._lastCursorLine || update.viewportChanged || update.focusChanged) {
          this.decorations = this.buildDecos(update.view);
        }
      }
    }

    buildDecos(view) {
      const widgets = [];
      const doc = view.state.doc;
      const hasFocus = view.hasFocus;
      const cursorLine = hasFocus ? doc.lineAt(view.state.selection.main.head).number : -1;
      this._lastCursorLine = cursorLine;
      // Reuse the cached full text — avoids a full string allocation on every cursor move
      const fullText = this._fullText ?? doc.toString();

      // --- <details> Blöcke als aufklappbare Karten (nur plain <details>, NICHT data-ref) ---
      const detailsSkipLines = new Set();
      const detailsRawLines = new Set(); // lines shown as raw text when cursor is inside block
      const detailsRegex = /<details(?:\s+data-ref="([^"]*)")?\s*>\s*\n\s*<summary>(.*?)<\/summary>\s*\n([\s\S]*?)\n\s*<\/details>/g;
      let dm;
      while ((dm = detailsRegex.exec(fullText)) !== null) {
        const ref = dm[1] || null;
        if (ref) continue; // Highlight-Blöcke werden via {{highlight:ref}} gehandhabt
        const startLine = doc.lineAt(dm.index).number;
        const endPos = Math.min(dm.index + dm[0].length - 1, doc.length - 1);
        const endLine = doc.lineAt(Math.max(0, endPos)).number;

        const cursorInBlock = cursorLine >= startLine && cursorLine <= endLine;

        if (!cursorInBlock) {
          for (let l = startLine; l <= endLine; l++) detailsSkipLines.add(l);
        } else {
          for (let l = startLine; l <= endLine; l++) detailsRawLines.add(l);
        }
      }

      // --- Helpers for spacing ---
      const lineText = (n) => (n >= 1 && n <= doc.lines) ? doc.line(n).text : '';
      const isListItem = (t) => /^(\s*)([-*]|\d+\.)\s/.test(t);
      const isHeadingLine = (t) => /^#{1,4}\s/.test(t);
      const isBlockquoteLine = (t) => t.startsWith('> ');
      const isEmptyLine = (t) => t.trim() === '';

      // --- Notion-style Block-Type Detection ---
      const blockType = (t) => {
        if (isHeadingLine(t)) return 'heading';
        if (isListItem(t)) return 'list';
        if (isBlockquoteLine(t)) return 'quote';
        if (isEmptyLine(t)) return 'empty';
        return 'text';
      };

      // --- Notion-style Spacing Matrix ---
      // Gleiche Typen clustern (kompakt), verschiedene Typen trennen (Atemraum).
      // Headings: viel Luft oben (neuer Abschnitt), wenig unten (Bindung an eigenen Inhalt).
      const SPACING = {
        'text→text':      { top: '2px',  bot: '0.55em' },
        'text→list':      { top: '10px', bot: '3px' },
        'text→heading':   { top: '20px', bot: '3px' },
        'text→quote':     { top: '12px', bot: '3px' },
        'text→empty':     { top: '2px',  bot: '5px' },
        'list→list':      { top: '3px',  bot: '3px' },
        'list→text':      { top: '12px', bot: '0.55em' },
        'list→heading':   { top: '20px', bot: '3px' },
        'list→quote':     { top: '12px', bot: '3px' },
        'list→empty':     { top: '3px',  bot: '5px' },
        'heading→text':   { top: '4px',  bot: '0.55em' },
        'heading→list':   { top: '6px',  bot: '3px' },
        'heading→heading':{ top: '20px', bot: '3px' },
        'heading→quote':  { top: '8px',  bot: '3px' },
        'heading→empty':  { top: '4px',  bot: '5px' },
        'quote→quote':    { top: '3px',  bot: '3px' },
        'quote→text':     { top: '14px', bot: '0.55em' },
        'quote→list':     { top: '10px', bot: '3px' },
        'quote→heading':  { top: '20px', bot: '3px' },
        'quote→empty':    { top: '3px',  bot: '5px' },
        'empty→text':     { top: '5px',  bot: '0.55em' },
        'empty→list':     { top: '5px',  bot: '3px' },
        'empty→heading':  { top: '5px',  bot: '3px' },
        'empty→quote':    { top: '5px',  bot: '3px' },
        'empty→empty':    { top: '5px',  bot: '5px' },
      };

      for (let i = 1; i <= doc.lines; i++) {
        if (detailsSkipLines.has(i)) continue;
        const line = doc.line(i);
        const text = line.text;
        const isActive = i === cursorLine;
        const cursorOffset = isActive ? view.state.selection.main.head - line.from : -1;

        // --- {{highlight:ref}} Einzeiler → DetailsWidget ---
        const hlMatch = text.match(/^\{\{highlight:([^}]+)\}\}$/);
        if (hlMatch && !isActive) {
          const hlRef = hlMatch[1];
          const getData = callbacksContainer?.getHighlightData;
          const data = getData ? getData(hlRef) : null;
          if (data) {
            const { onOpenInBibliothek, onRemoveHighlight, onDragOutHighlight, onMoveHighlight } = callbacksContainer;
            widgets.push(Decoration.replace({
              widget: new DetailsWidget(
                `${data.author}: ${data.title}`,
                `> "${data.text}"`,
                hlRef,
                onOpenInBibliothek,
                onRemoveHighlight,
                onMoveHighlight,
                onDragOutHighlight
              )
            }).range(line.from, line.to));
          }
          continue; // Skip normal line processing for this line
        }

        // --- Notion-style adjacency-based spacing ---
        // Padding wird durch den Übergang prevType→currentType bestimmt.
        // Bottom-Padding durch currentType→nextType (Lookahead).
        {
          const prevText = lineText(i - 1);
          const nextText = lineText(i + 1);
          const prevType = blockType(prevText);
          const currType = blockType(text);
          const nextType = (i < doc.lines) ? blockType(nextText) : 'empty';

          // Erste Content-Zeile (i===1, direkt nach Frontmatter): wenig Luft oben
          const topKey = (i === 1) ? 'empty→' + currType : prevType + '→' + currType;
          const botKey = currType + '→' + nextType;

          const topSpacing = SPACING[topKey] || SPACING['text→text'];
          const botSpacing = SPACING[botKey] || SPACING['text→text'];

          // Erste Zeile: reduziertes Top-Padding
          const ptop = (i === 1) ? '4px' : topSpacing.top;
          const pbot = botSpacing.bot;

          widgets.push(Decoration.line({
            attributes: { style: `padding-top:${ptop};padding-bottom:${pbot}` }
          }).range(line.from));
        }

        // --- Inline formatting: nur wenn Zeile NICHT aktiv (damit man Markdown editieren kann) ---
        if (!isActive) {
          // --- Bold: **text** — Marker verstecken, Text mit CSS stylen (klickbar) ---
          for (const m of text.matchAll(/\*\*([^*]+)\*\*/g)) {
            const from = line.from + m.index;
            const end = from + m[0].length;
            widgets.push(Decoration.replace({}).range(from, from + 2));              // ** am Anfang
            widgets.push(Decoration.mark({ class: 'cm-preview-bold' }).range(from + 2, end - 2)); // text
            widgets.push(Decoration.replace({}).range(end - 2, end));                // ** am Ende
          }

          // --- Italic: *text* (nicht **) — Marker verstecken, Text mit CSS stylen ---
          for (const m of text.matchAll(/(?<!\*)\*([^*]+)\*(?!\*)/g)) {
            const from = line.from + m.index;
            const end = from + m[0].length;
            widgets.push(Decoration.replace({}).range(from, from + 1));              // * am Anfang
            widgets.push(Decoration.mark({ class: 'cm-preview-italic' }).range(from + 1, end - 1)); // text
            widgets.push(Decoration.replace({}).range(end - 1, end));                // * am Ende
          }

          // --- Highlight: ==text== — Marker verstecken, Text mit CSS stylen ---
          for (const m of text.matchAll(/==([^=]+)==/g)) {
            const from = line.from + m.index;
            const end = from + m[0].length;
            widgets.push(Decoration.replace({}).range(from, from + 2));              // == am Anfang
            widgets.push(Decoration.mark({ class: 'cm-preview-highlight' }).range(from + 2, end - 2)); // text
            widgets.push(Decoration.replace({}).range(end - 2, end));                // == am Ende
          }

          // --- Bild: ![alt](url) ---
          const imgMatch = text.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
          if (imgMatch) {
            widgets.push(Decoration.replace({
              widget: new ImageWidget(imgMatch[2], imgMatch[1])
            }).range(line.from, line.to));
          }

          // --- Heading: # text (h1–h4) ---
          if (text.match(/^#{1,4}\s/)) {
            const level = text.match(/^(#+)/)[1].length;
            const sizes = { 1: '1.6em', 2: '1.3em', 3: '1.1em', 4: '1.0em' };
            const weights = { 1: '800', 2: '700', 3: '600', 4: '600' };
            const opacities = { 1: '1', 2: '0.92', 3: '0.85', 4: '0.75' };
            widgets.push(Decoration.line({
              attributes: { style: `font-size:${sizes[level]};font-weight:${weights[level]};letter-spacing:-0.02em;opacity:${opacities[level]}` }
            }).range(line.from));
            widgets.push(Decoration.replace({}).range(line.from, line.from + level + 1));
          }

          // --- Trennlinie: --- ---
          if (text.match(/^-{3,}$/)) {
            widgets.push(Decoration.replace({ widget: new class extends WidgetType {
              toDOM() {
                const hr = document.createElement('hr');
                hr.style.cssText = 'border:none;border-top:1px solid var(--border);margin:-20px 0 4px 0';
                return hr;
              }
            } }).range(line.from, line.to));
          }
        }

        // --- Blockquote: > text ---
        if (!detailsRawLines.has(i) && text.startsWith('> ')) {
          widgets.push(Decoration.line({
            attributes: { style: 'border-left:3px solid var(--accent);padding-left:14px;color:var(--text-secondary);font-style:italic' }
          }).range(line.from));
          // Hide the '> ' prefix when cursor is not on this line
          if (!isActive) {
            widgets.push(Decoration.replace({}).range(line.from, line.from + 2));
          }
        }

        // --- Unordered List: - item oder * item (immer — nur visuell) ---
        {
          const ulMatch = text.match(/^(\s*)([-*])\s+(.*)/);
          if (ulMatch) {
            const indent = ulMatch[1].length;
            const depth = Math.floor(indent / 2);
            const bulletChar = depth === 0 ? '•' : '◦';
            widgets.push(Decoration.line({
              attributes: { style: `padding-left:${(depth * 20) + 8}px;` }
            }).range(line.from));
            if (!isActive) {
              const prefixFrom = line.from;
              const prefixTo = line.from + indent + ulMatch[2].length + 1;
              widgets.push(Decoration.replace({ widget: new class extends WidgetType {
                toDOM() {
                  const el = document.createElement('span');
                  el.textContent = bulletChar + ' ';
                  el.style.cssText = `color:var(--color-text-secondary,#999);font-weight:400;font-size:${depth === 0 ? '1.05em' : '0.9em'};margin-right:4px;position:relative;top:${depth === 0 ? '0.04em' : '0.08em'}`;
                  return el;
                }
              } }).range(prefixFrom, prefixTo));
            }
          }
        }

        // --- Ordered List: 1. item (immer — nur visuell) ---
        {
          const olMatch = text.match(/^(\s*)(\d+)\.\s+(.*)/);
          if (olMatch) {
            const indent = olMatch[1].length;
            const depth = Math.floor(indent / 2);
            const num = olMatch[2];
            widgets.push(Decoration.line({
              attributes: { style: `padding-left:${(depth * 20) + 8}px;` }
            }).range(line.from));
            if (!isActive) {
              const prefixFrom = line.from;
              const prefixTo = line.from + indent + num.length + 2;
              widgets.push(Decoration.replace({ widget: new class extends WidgetType {
                toDOM() {
                  const el = document.createElement('span');
                  el.textContent = num + '. ';
                  el.style.cssText = 'color:var(--color-text-secondary,#999);font-weight:500;font-size:0.82em;margin-right:5px;font-variant-numeric:tabular-nums;letter-spacing:-0.01em';
                  return el;
                }
              } }).range(prefixFrom, prefixTo));
            }
          }
        }

        // --- Wiki-Link: [[id]] or [[id|display text]] --- IMMER rendern, auch auf aktiver Zeile ---
        for (const m of text.matchAll(/\[\[([^\]|]+)(?:\|([^\]]*))?\]\]/g)) {
          // Auf aktiver Zeile: nur rendern wenn Cursor NICHT im Link steht
          const matchStart = m.index;
          const matchEnd = m.index + m[0].length;
          if (isActive && cursorOffset >= matchStart && cursorOffset <= matchEnd) continue;

          const from = line.from + m.index;
          const linkId = m[1];
          const displayText = m[2] || null;
          const linked = _allZettelForPlugin.find(z => z.id === linkId);
          const title = linked?.title || null;
          widgets.push(Decoration.replace({
            widget: new WikiLinkWidget(linkId, displayText, title)
          }).range(from, from + m[0].length));
        }
      }

      widgets.sort((a, b) => a.from - b.from || a.startSide - b.startSide);
      return Decoration.set(widgets, true);
    }
  }, { decorations: v => v.decorations });
}

// --- Theme ---
function zettelTheme() {
  return EditorView.theme({
    '&': {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif',
      fontSize: '1.02rem',
      lineHeight: '1.75',
      color: 'var(--text-content)',
      backgroundColor: 'transparent',
      // no height: 100% — let editor size to content so backlinks flow below
    },
    '&.cm-focused': { outline: 'none' },
    '.cm-scroller': { overflow: 'visible' },
    '.cm-content': { padding: '0 0 32px 0', caretColor: 'var(--accent)', minHeight: '200px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif', lineHeight: '1.72' },
    '.cm-line': { padding: '0' },
    '.cm-cursor': { borderLeftColor: 'var(--accent)', borderLeftWidth: '2px' },
    '.cm-selectionBackground': { background: 'var(--accent-bg) !important' },
    '&.cm-focused .cm-selectionBackground': { background: 'var(--accent-bg-hover) !important' },
    '.cm-gutters': { display: 'none' },
    '.cm-activeLine': { backgroundColor: 'transparent' },
    '.cm-activeLineGutter': { backgroundColor: 'transparent' },
  });
}

// --- Formatting Keymaps ---
function wrapSelection(view, marker) {
  const { from, to } = view.state.selection.main;
  if (from === to) return false;
  const selected = view.state.sliceDoc(from, to);
  view.dispatch({
    changes: { from, to, insert: marker + selected + marker },
    selection: { anchor: from + marker.length, head: to + marker.length }
  });
  return true;
}

function formatAsQuote(view) {
  const { from, to } = view.state.selection.main;
  if (from === to) {
    // No selection: insert quote template
    const template = `> „Zitat"\n> — Autor`;
    view.dispatch({
      changes: { from, to, insert: template },
      // Select "Zitat" so user can immediately type over it
      selection: { anchor: from + 3, head: from + 3 + 5 }
    });
    return true;
  }
  const selected = view.state.sliceDoc(from, to);
  const quote = `> „${selected}"\n> — Autor`;
  view.dispatch({
    changes: { from, to, insert: quote },
    // Place cursor at "Autor" and select it for easy replacement
    selection: { anchor: from + quote.length - 5, head: from + quote.length }
  });
  return true;
}

// --- Indent / Unindent helpers ---
function indentLines(view, direction) {
  const { state } = view;
  const sel = state.selection.main;
  const fromLine = state.doc.lineAt(sel.from);
  const toLine = state.doc.lineAt(sel.to);

  const changes = [];
  let anchorDelta = 0;
  let totalDelta = 0;

  for (let n = fromLine.number; n <= toLine.number; n++) {
    const ln = state.doc.line(n);
    if (direction === 'in') {
      changes.push({ from: ln.from, insert: '  ' });
      if (n === fromLine.number) anchorDelta = 2;
      totalDelta += 2;
    } else {
      const m = ln.text.match(/^( {1,2})/);
      if (m) {
        changes.push({ from: ln.from, to: ln.from + m[1].length });
        if (n === fromLine.number) anchorDelta = -m[1].length;
        totalDelta -= m[1].length;
      }
    }
  }

  if (changes.length === 0) return direction === 'out'; // Shift-Tab always consumes
  const isCaret = sel.from === sel.to;
  view.dispatch({
    changes,
    selection: {
      anchor: Math.max(fromLine.from, sel.from + anchorDelta),
      head: isCaret
        ? Math.max(fromLine.from, sel.from + anchorDelta)
        : Math.max(toLine.from, sel.to + totalDelta),
    },
  });
  return true;
}

// Wrap selection with marker if text is selected; if already wrapped, toggle off
function wrapOrUnwrap(view, marker) {
  const { state } = view;
  const { from, to } = state.selection.main;
  if (from === to) return false; // no selection → let default handle the character
  const selected = state.sliceDoc(from, to);
  const m = marker.length;
  // Already wrapped → unwrap
  if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length >= m * 2 + 1) {
    const inner = selected.slice(m, -m);
    view.dispatch({
      changes: { from, to, insert: inner },
      selection: { anchor: from, head: from + inner.length },
    });
    return true;
  }
  // Wrap
  view.dispatch({
    changes: { from, to, insert: marker + selected + marker },
    selection: { anchor: from, head: to + m * 2 },
  });
  return true;
}

// * with selection: single star = italic; if already italic (*text*) → upgrade to bold (**text**)
function handleStar(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  if (from === to) return false;
  const selected = state.sliceDoc(from, to);
  // Already bold? → unwrap to italic
  if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 5) {
    const inner = selected.slice(2, -2);
    view.dispatch({
      changes: { from, to, insert: `*${inner}*` },
      selection: { anchor: from, head: from + inner.length + 2 },
    });
    return true;
  }
  // Already italic? → upgrade to bold
  if (selected.startsWith('*') && selected.endsWith('*') && selected.length >= 3) {
    const inner = selected.slice(1, -1);
    view.dispatch({
      changes: { from, to, insert: `**${inner}**` },
      selection: { anchor: from, head: from + inner.length + 4 },
    });
    return true;
  }
  // Plain text → italic
  view.dispatch({
    changes: { from, to, insert: `*${selected}*` },
    selection: { anchor: from, head: to + 2 },
  });
  return true;
}

// = with selection:
//   1st press  → =text=   (selection stays on inner text, first = is visible)
//   2nd press  → ==text== (detects flanking = chars, upgrades to full highlight)
//   on ==text== selected → unwrap
//   without selection   → type = normally
function handleEquals(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  if (from === to) return false; // no selection → type = normally

  const selected = state.sliceDoc(from, to);

  // Already fully wrapped ==text== → unwrap
  if (selected.startsWith('==') && selected.endsWith('==') && selected.length >= 5) {
    const inner = selected.slice(2, -2);
    view.dispatch({
      changes: { from, to, insert: inner },
      selection: { anchor: from, head: from + inner.length },
    });
    return true;
  }

  // Second = press: flanking single = chars already present?
  const charBefore = from > 0 ? state.sliceDoc(from - 1, from) : '';
  const charAfter  = to < state.doc.length ? state.sliceDoc(to, to + 1) : '';
  if (charBefore === '=' && charAfter === '=') {
    // =text= → ==text==
    view.dispatch({
      changes: { from: from - 1, to: to + 1, insert: `==${selected}==` },
      selection: { anchor: from - 1, head: from - 1 + selected.length + 4 },
    });
    return true;
  }

  // First = press: insert single = on each side, keep inner text selected
  view.dispatch({
    changes: { from, to, insert: `=${selected}=` },
    selection: { anchor: from + 1, head: from + 1 + selected.length },
  });
  return true;
}

// Option+Enter / Cmd+Enter: Neue Zeile unterhalb der aktuellen einfügen (wie Bear)
function handleInsertLineBelow(view) {
  const { state } = view;
  const { head } = state.selection.main;
  const line = state.doc.lineAt(head);
  view.dispatch({
    changes: { from: line.to, insert: '\n' },
    selection: { anchor: line.to + 1 }
  });
  return true;
}

// Continue list item on Enter — works for unordered (- / *) and ordered (1.) lists
// Empty list item → exit the list (removes the marker, leaves a blank line)
function handleListEnter(view) {
  const state = view.state;
  const { from, to } = state.selection.main;
  const line = state.doc.lineAt(from);

  // Unordered list: optional indent + "- " or "* "
  const ulMatch = line.text.match(/^(\s*)([-*]) (.*)/);
  if (ulMatch) {
    const [, indent, bullet, content] = ulMatch;
    const markerEnd = line.from + indent.length + 2; // position after "- "
    // Cursor at/after marker, content empty → exit list
    if (from >= markerEnd && !content.trim()) {
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: '' },
        selection: { anchor: line.from }
      });
      return true;
    }
    const prefix = `${indent}${bullet} `;
    view.dispatch({
      changes: { from, to, insert: '\n' + prefix },
      selection: { anchor: from + 1 + prefix.length }
    });
    return true;
  }

  // Ordered list: optional indent + "1. "
  const olMatch = line.text.match(/^(\s*)(\d+)\. (.*)/);
  if (olMatch) {
    const [, indent, num, content] = olMatch;
    const markerEnd = line.from + indent.length + num.length + 2;
    // Empty item → exit list
    if (from >= markerEnd && !content.trim()) {
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: '' },
        selection: { anchor: line.from }
      });
      return true;
    }
    const prefix = `${indent}${parseInt(num, 10) + 1}. `;
    view.dispatch({
      changes: { from, to, insert: '\n' + prefix },
      selection: { anchor: from + 1 + prefix.length }
    });
    return true;
  }

  return false; // not a list line → fall through to next handler
}

// Continue blockquote on Enter / Shift-Enter when cursor is on a '> ' line
function handleBlockquoteEnter(view) {
  const state = view.state;
  const { from } = state.selection.main;
  const line = state.doc.lineAt(from);
  if (!line.text.startsWith('> ')) return false;
  // If the line is only '> ' (empty quote line), exit the blockquote instead
  if (line.text === '> ') {
    view.dispatch({
      changes: { from: line.from, to: line.to, insert: '' },
      selection: { anchor: line.from }
    });
    return true;
  }
  view.dispatch({
    changes: { from, insert: '\n> ' },
    selection: { anchor: from + 3 }
  });
  return true;
}

const formattingKeymap = keymap.of([
  { key: 'Mod-b', run: (view) => wrapSelection(view, '**') },
  { key: 'Mod-i', run: (view) => wrapSelection(view, '*') },
  { key: 'Mod-Shift-z', run: formatAsQuote },
  { key: '*',     run: handleStar },
  { key: '=',     run: handleEquals },
  { key: 'Tab',         run: (view) => indentLines(view, 'in') },
  { key: 'Shift-Tab',   run: (view) => indentLines(view, 'out') },
  { key: 'Alt-Enter',   run: handleInsertLineBelow },
  { key: 'Mod-Enter',   run: handleInsertLineBelow },
  { key: 'Enter',       run: handleListEnter },
  { key: 'Enter',       run: handleBlockquoteEnter },
  { key: 'Shift-Enter', run: handleBlockquoteEnter },
]);

// --- Wiki-Link Autocomplete Helper ---
function getWikiLinkContext(state) {
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const textBefore = line.text.slice(0, pos - line.from);
  const openIdx = textBefore.lastIndexOf('[[');
  if (openIdx === -1) return null;
  const afterOpen = textBefore.slice(openIdx + 2);
  if (afterOpen.includes(']]')) return null;
  // Check if this is after a pipe (then user is typing display text, not searching)
  if (afterOpen.includes('|')) return null;
  return {
    query: afterOpen,
    from: line.from + openIdx,
  };
}

// --- Hauptkomponente ---
const LiveEditor = forwardRef(function LiveEditor({ value, onChange, onDrop, placeholder, cursorAt, highlightQuote, allZettel, onNavigate, onOpenInBibliothek, onRemoveHighlight, onDragOutHighlight, onMoveHighlight, getHighlightData, readOnly = false }, ref) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [hlMoveLine, setHlMoveLine] = useState(null); // { top, ref } für Insert-Line bei Editor-internem DnD
  const hlMoveLineRef = useRef(null); // Verhindert setState-Spam


  useImperativeHandle(ref, () => ({
    focus() {
      const view = viewRef.current;
      if (!view) return;
      const endPos = view.state.doc.length;
      view.dispatch({ selection: { anchor: endPos } });
      view.focus();
    }
  }));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const allZettelRef = useRef(allZettel || []);
  allZettelRef.current = allZettel || [];

  // Refs für Highlight-Callbacks — verhindert stale closures im CodeMirror-Plugin
  const onOpenInBibliothekRef = useRef(onOpenInBibliothek);
  onOpenInBibliothekRef.current = onOpenInBibliothek;
  const onRemoveHighlightRef = useRef(onRemoveHighlight);
  onRemoveHighlightRef.current = onRemoveHighlight;
  const onDragOutHighlightRef = useRef(onDragOutHighlight);
  onDragOutHighlightRef.current = onDragOutHighlight;
  const onMoveHighlightRef = useRef(onMoveHighlight);
  onMoveHighlightRef.current = onMoveHighlight;
  const getHighlightDataRef = useRef(getHighlightData);
  getHighlightDataRef.current = getHighlightData;

  // Keep global ref in sync for the plugin
  _allZettelForPlugin = allZettel || [];

  // Force CM6 to re-decorate when allZettel changes (titles update)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    // Trigger a dummy transaction to force decoration rebuild
    view.dispatch({ effects: [] });
  }, [allZettel]);

  // Autocomplete state
  const [acState, setAcState] = useState({ open: false, query: '', from: 0, activeIndex: 0, coords: null, selectedText: '' });
  const acStateRef = useRef(acState);
  acStateRef.current = acState;

  // Compute results — derived from acState
  const acResults = (() => {
    if (!acState.open) return [];
    const q = acState.query.toLowerCase();
    const all = allZettelRef.current;
    return q
      ? all.filter(z => z.id.toLowerCase().includes(q) || z.title?.toLowerCase().includes(q)).slice(0, 8)
      : all.slice(0, 8);
  })();
  const acResultsRef = useRef(acResults);
  acResultsRef.current = acResults;

  // Insert selected link
  const insertLink = useCallback((zettelItem) => {
    const view = viewRef.current;
    if (!view) return;
    const ac = acStateRef.current;
    const cursor = view.state.selection.main.head;
    // If there was selected text before typing [[, use it as display text
    const displayPart = ac.selectedText ? `|${ac.selectedText}` : '';
    const insertText = `[[${zettelItem.id}${displayPart}]]`;
    view.dispatch({
      changes: { from: ac.from, to: cursor, insert: insertText },
      selection: { anchor: ac.from + insertText.length }
    });
    view.focus();
    setAcState({ open: false, query: '', from: 0, activeIndex: 0, coords: null, selectedText: '' });
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    // Track if user had a selection before typing [[
    let pendingSelectedText = '';

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChangeRef.current(update.state.doc.toString());

        // Detect if [[ was just typed over a selection
        update.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
          const ins = inserted.toString();
          if (ins === '[[' || ins === '[') {
            // Check if text was replaced (selection was present)
            if (toA > fromA) {
              const replaced = update.startState.sliceDoc(fromA, toA);
              if (replaced.length > 0 && ins === '[[') {
                pendingSelectedText = replaced;
              }
            }
          }
        });
      }

      if (update.docChanged || update.selectionSet) {
        const ctx = getWikiLinkContext(update.state);
        if (ctx) {
          let coords = null;
          try {
            coords = update.view.coordsAtPos(update.state.selection.main.head);
          } catch (e) { /* ignore */ }
          if (!coords && update.view.dom) {
            const rect = update.view.dom.getBoundingClientRect();
            coords = { left: rect.left + 20, bottom: rect.top + 40 };
          }
          setAcState(prev => ({
            ...prev,
            open: true,
            query: ctx.query,
            from: ctx.from,
            coords: coords ? { left: coords.left, top: (coords.bottom || coords.top) + 4 } : prev.coords,
            selectedText: pendingSelectedText || prev.selectedText,
          }));
          // Reset after use
          if (pendingSelectedText) pendingSelectedText = '';
        } else {
          setAcState(prev => prev.open ? { open: false, query: '', from: 0, activeIndex: 0, coords: null, selectedText: '' } : prev);
        }
      }
      // Focus-Tracking für StateField: bei Blur/Focus Details-Dekorationen neu berechnen
      if (update.focusChanged) {
        const hasFocus = update.view.hasFocus;
        const v = update.view;
        setTimeout(() => {
          try { v.dispatch({ effects: focusChangedEffect.of(hasFocus) }); } catch (_) {}
        }, 0);
      }
    });

    // Keymap that intercepts arrow keys / Enter / Escape when autocomplete is open
    const acKeymap = keymap.of([
      {
        key: 'ArrowDown',
        run: () => {
          const ac = acStateRef.current;
          const results = acResultsRef.current;
          if (!ac.open || results.length === 0) return false;
          setAcState(prev => ({ ...prev, activeIndex: Math.min(prev.activeIndex + 1, results.length - 1) }));
          return true;
        }
      },
      {
        key: 'ArrowUp',
        run: () => {
          const ac = acStateRef.current;
          if (!ac.open || acResultsRef.current.length === 0) return false;
          setAcState(prev => ({ ...prev, activeIndex: Math.max(prev.activeIndex - 1, 0) }));
          return true;
        }
      },
      {
        key: 'Enter',
        run: () => {
          const ac = acStateRef.current;
          const results = acResultsRef.current;
          if (!ac.open || results.length === 0) return false;
          insertLink(results[ac.activeIndex]);
          return true;
        }
      },
      {
        key: 'Escape',
        run: () => {
          const ac = acStateRef.current;
          if (!ac.open) return false;
          setAcState({ open: false, query: '', from: 0, activeIndex: 0, coords: null, selectedText: '' });
          return true;
        }
      },
      {
        key: 'Tab',
        run: () => {
          const ac = acStateRef.current;
          const results = acResultsRef.current;
          if (!ac.open || results.length === 0) return false;
          insertLink(results[ac.activeIndex]);
          return true;
        }
      },
    ]);

    // Mutable callbacks container — always has fresh refs, used by StateField + ViewPlugin
    const detailsCallbacks = {
      onOpenInBibliothek: (...args) => onOpenInBibliothekRef.current?.(...args),
      onRemoveHighlight: (...args) => onRemoveHighlightRef.current?.(...args),
      onDragOutHighlight: (...args) => onDragOutHighlightRef.current?.(...args),
      onMoveHighlight: (...args) => onMoveHighlightRef.current?.(...args),
      getHighlightData: (...args) => getHighlightDataRef.current?.(...args),
    };

    const state = EditorState.create({
      doc: value || '',
      extensions: [
        EditorView.editable.of(!readOnly),
        EditorState.readOnly.of(readOnly),
        history(),
        acKeymap,
        formattingKeymap,
        keymap.of([...defaultKeymap, ...historyKeymap]),
        markdown(),
        // Auto-expand "> " at line start into full quote template
        EditorView.inputHandler.of((view, from, to, text) => {
          if (text !== ' ') return false;
          const line = view.state.doc.lineAt(from);
          const before = view.state.sliceDoc(line.from, from);
          if (before !== '>') return false;
          // Replace "> " with full quote template
          const template = `> „Zitat"\n> — Autor`;
          view.dispatch({
            changes: { from: line.from, to, insert: template },
            // Select "Zitat" so user can immediately type
            selection: { anchor: line.from + 3, head: line.from + 3 + 5 }
          });
          return true;
        }),
        // Multi-Zeilen-Dekorationen (plain Details-Blöcke) via StateField — einzige CM6-konforme Lösung
        hasFocusField,
        makeDetailsField(detailsCallbacks),
        livePreviewPlugin(detailsCallbacks),
        placeholder ? cmPlaceholder(placeholder) : [],
        zettelTheme(),
        updateListener,
        EditorView.lineWrapping,
        EditorView.domEventHandlers({
          mousedown: (event, view) => {
            // 1) Check if clicked on a rendered wiki-link widget (before cursor moves)
            const target = event.target.closest('.wikilink-rendered');
            if (target && target.dataset.linkId) {
              event.preventDefault();
              event.stopPropagation();
              const nav = onNavigateRef.current;
              if (nav) nav(target.dataset.linkId);
              return true;
            }
            // 2) Check if clicked inside raw [[...]] text (cursor already on this line)
            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos != null) {
              const lineText = view.state.doc.lineAt(pos).text;
              const lineStart = view.state.doc.lineAt(pos).from;
              const offsetInLine = pos - lineStart;
              const wikiRe = /\[\[([^\]|]+)(?:\|[^\]]*)?\]\]/g;
              let wm;
              while ((wm = wikiRe.exec(lineText)) !== null) {
                if (offsetInLine >= wm.index && offsetInLine <= wm.index + wm[0].length) {
                  event.preventDefault();
                  event.stopPropagation();
                  const nav = onNavigateRef.current;
                  if (nav) nav(wm[1]);
                  return true;
                }
              }
            }
            return false;
          },
          mousemove: (event, view) => {
            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos != null) {
              const line = view.state.doc.lineAt(pos);
              const offsetInLine = pos - line.from;
              const wikiRe = /\[\[([^\]|]+)(?:\|[^\]]*)?\]\]/g;
              let wm, overLink = false;
              while ((wm = wikiRe.exec(line.text)) !== null) {
                if (offsetInLine >= wm.index && offsetInLine <= wm.index + wm[0].length) {
                  overLink = true;
                  break;
                }
              }
              view.contentDOM.style.cursor = overLink ? 'pointer' : '';
            }
            return false;
          },
          drop: (event, view) => {
            const files = [...event.dataTransfer.files].filter(f => f.type.startsWith('image/'));
            if (files.length === 0) return false;
            event.preventDefault();
            if (onDrop) onDrop(files, view);
            return true;
          },
          dragover: (event) => {
            if (event.dataTransfer?.types.includes('Files')) {
              event.preventDefault();
              return true;
            }
            return false;
          }
        }),
      ]
    });

    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;

    return () => view.destroy();
  }, []); // eslint-disable-line

  // Content von außen aktualisieren (wenn Zettel wechselt)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value || '' }
      });
    }
  }, [value]);

  // Cursor an bestimmte Position setzen
  useEffect(() => {
    const view = viewRef.current;
    if (!view || !cursorAt || cursorAt <= 0) return;
    const pos = Math.min(cursorAt, view.state.doc.length);
    view.dispatch({ selection: { anchor: pos } });
    view.focus();
  }, [cursorAt]);

  // Zitat im Editor finden und hin-scrollen
  useEffect(() => {
    if (!highlightQuote) return;
    const timer = setTimeout(() => {
      const view = viewRef.current;
      if (!view) return;
      const doc = view.state.doc.toString();
      const searchText = highlightQuote.replace(/[„""\u201E\u201C\u201D]/g, '').trim();
      const idx = doc.indexOf(searchText);
      if (idx < 0) return;
      const line = view.state.doc.lineAt(idx);
      view.dispatch({
        effects: EditorView.scrollIntoView(line.from, { y: 'center' })
      });
    }, 250); // Warten bis CM6 mit neuem Content initialisiert ist
    return () => clearTimeout(timer);
  }, [highlightQuote]);

  return (
    <>
      <div
        ref={containerRef}
        className="live-editor-container"
        style={{ cursor: 'text', position: 'relative' }}
        onClick={(e) => {
          const view = viewRef.current;
          if (!view) return;
          const cmContent = containerRef.current?.querySelector('.cm-content');
          if (cmContent && !cmContent.contains(e.target) && !e.target.closest('.cm-content')) {
            e.preventDefault();
            const endPos = view.state.doc.length;
            view.dispatch({ selection: { anchor: endPos } });
            view.focus();
          }
        }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('application/highlight-editor-move')) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          const container = containerRef.current;
          const view = viewRef.current;
          if (!container || !view) return;

          const y = e.clientY;
          const containerRect = container.getBoundingClientRect();
          const allCmLines = container.querySelectorAll('.cm-line');

          let closestRef = null;
          let closestTop = null;
          let closestLineText = null; // Zeilentext als Anker (stabiler als char-offset)
          let lastRect = null;

          for (const cmLine of allCmLines) {
            const rect = cmLine.getBoundingClientRect();
            const mid = rect.top + rect.height / 2;
            if (y < mid) {
              closestTop = rect.top - containerRect.top;
              // Liegt ein Highlight-Block in dieser Zeile?
              const detailsEl = cmLine.querySelector('details[data-highlight-ref]');
              if (detailsEl) {
                closestRef = detailsEl.dataset.highlightRef;
                closestLineText = null;
              } else {
                // Zeilentext als Anker erfassen
                const pos = view.posAtCoords({ x: rect.left + 4, y: rect.top + 4 }, false);
                if (pos != null) {
                  let lineObj = view.state.doc.lineAt(pos);
                  // Bei Leerzeilen: zur nächsten nicht-leeren Zeile gehen
                  while (lineObj.text.trim() === '' && lineObj.number < view.state.doc.lines) {
                    const next = lineObj.to + 1;
                    if (next > view.state.doc.length) break;
                    lineObj = view.state.doc.lineAt(next);
                  }
                  closestLineText = lineObj.text;
                }
                closestRef = null;
              }
              break;
            }
            lastRect = rect;
          }

          // Cursor unterhalb aller Zeilen → ans Ende
          if (closestTop === null && lastRect) {
            closestTop = lastRect.bottom - containerRect.top;
          }

          const snappedTop = closestTop != null ? Math.round(closestTop / 10) * 10 : null;
          const key = `${closestRef}:${closestLineText}:${snappedTop}`;
          if (hlMoveLineRef.current !== key) {
            hlMoveLineRef.current = key;
            setHlMoveLine({ top: closestTop, ref: closestRef, lineText: closestLineText });
          }
        }}
        onDragLeave={(e) => {
          if (!e.dataTransfer.types.includes('application/highlight-editor-move')) return;
          const container = containerRef.current;
          if (container && !container.contains(e.relatedTarget)) {
            setHlMoveLine(null);
            hlMoveLineRef.current = null;
          }
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('application/highlight-editor-move')) return;
          e.preventDefault();
          const movingRef = e.dataTransfer.getData('application/highlight-editor-move');
          const targetRef = hlMoveLine?.ref || null;
          const targetLineText = hlMoveLine?.lineText ?? null;
          setHlMoveLine(null);
          hlMoveLineRef.current = null;
          // Flag setzen: Drop wurde intern verarbeitet → dragend soll nicht löschen
          const container = containerRef.current;
          if (container) {
            const srcEl = container.querySelector(`details[data-highlight-ref="${movingRef}"]`);
            if (srcEl?._markDropHandled) srcEl._markDropHandled();
          }
          if (movingRef && onMoveHighlight && movingRef !== targetRef) {
            onMoveHighlight(movingRef, targetRef, targetLineText);
          }
        }}
      >
        {/* Insert-Line für Editor-internes Highlight-Verschieben */}
        {hlMoveLine && hlMoveLine.top != null && (
          <div className="hl-editor-move-line" style={{ top: hlMoveLine.top }} />
        )}
      </div>
      {acState.open && acState.coords && ReactDOM.createPortal(
        <div
          className="wikilink-autocomplete"
          style={{
            position: 'fixed',
            left: Math.min(acState.coords.left, window.innerWidth - 320),
            top: Math.min(acState.coords.top, window.innerHeight - 300),
            zIndex: 1000,
          }}
        >
          {acResults.length === 0 ? (
            <div className="wikilink-ac-empty">Keine Notizen gefunden</div>
          ) : (
            acResults.map((z, i) => (
              <div
                key={z.id}
                className={`wikilink-ac-item ${i === acState.activeIndex ? 'active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); insertLink(z); }}
                onMouseEnter={() => setAcState(prev => ({ ...prev, activeIndex: i }))}
              >
                <span className="wikilink-ac-id">{z.id}</span>
                <span className="wikilink-ac-title">{z.title || '(Ohne Titel)'}</span>
              </div>
            ))
          )}
          <div className="wikilink-ac-hint">
            <kbd>↑↓</kbd> navigieren · <kbd>↵</kbd> einfügen · <kbd>Esc</kbd> schließen
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

export default LiveEditor;
